// Core
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import {
  Chip,
  Crumbs,
  DateRangePopover,
  DonutCard,
  KpiStrip,
  LineChart,
  PageHead,
  RemoteTable,
  Segmented,
  SideTabs,
  Tabs,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import DeleteRecordsModal from "components/Modals/DeleteRecordsModal";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  selectTimeSelector,
  updateTimeSelector,
} from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMeters, useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";
import {
  useAddNewLoadsMutation,
  useDeleteLoadsMutation,
  useGetAggregatedLoadsByCommunityIdQuery,
  useGetLoadsByCommunityIdPaginationQuery,
  useGetLoadsKpisByCommunityIdQuery,
  useGetMetersLoadsByCommunityIdQuery,
} from "state/loads/loadsApiSlice";
import {
  useAddNewLoadsForecastMutation,
  useDeleteLoadsForecastMutation,
  useGetAggregatedLoadsForecastByCommunityIdQuery,
  useGetLoadsForecastByCommunityIdPaginationQuery,
  useGetLoadsForecastKpisByCommunityIdQuery,
  useGetMetersLoadsForecastByCommunityIdQuery,
} from "state/loadsForecast/loadsApiSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";
import { createChartXAxisLabel } from "utils/charts";
import { datetimeToTime, formatDate } from "utils/time";

// The chart aggregates to daily resolution; the table keeps the raw DB
// granularity (15 min) via its own paginated query.
const CHART_GRANULARITY = "DAY";

const fmt = (v) =>
  v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—";

const HOOKS = {
  actual: {
    kpisQuery: useGetLoadsKpisByCommunityIdQuery,
    aggQuery: useGetAggregatedLoadsByCommunityIdQuery,
    metersAggQuery: useGetMetersLoadsByCommunityIdQuery,
    paginationQuery: useGetLoadsByCommunityIdPaginationQuery,
  },
  forecast: {
    kpisQuery: useGetLoadsForecastKpisByCommunityIdQuery,
    aggQuery: useGetAggregatedLoadsForecastByCommunityIdQuery,
    metersAggQuery: useGetMetersLoadsForecastByCommunityIdQuery,
    paginationQuery: useGetLoadsForecastByCommunityIdPaginationQuery,
  },
};

/* One side (actual or forecast) of the measurements view — design's
   MeasurementsSubView. Mounted with key={side}, so the hook set is constant
   for the lifetime of each instance. */
function MeasurementsSubView({ hooks, scope, sideLabel, onUpload, isUploading }) {
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["internalLoads", "common"]);
  const { startDate, endDate } = useSelector(selectTimeSelector);

  const [tab, setTab] = useState("charts");
  const [view, setView] = useState("net"); // net | cg
  const [chartMeterId, setChartMeterId] = useState("");

  const displayCPEs = scope === "cpes";

  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ meters: (data && selectAllMeters(data)) || [] }),
    }
  );
  const meterName = (id) => meters.find((m) => m.id === id)?.identifier ?? "—";

  // --- analytics queries ---
  const chartArgs = { communityId: community.id, startDate, endDate };
  // Historical analytics — refetch on range change, not on a 15s poll/focus
  // (the poll re-fired backend errors and hammered heavy aggregations).
  const chartOpts = { refetchOnMountOrArgChange: true };
  const { data: kpis } = hooks.kpisQuery(chartArgs, chartOpts);
  const { data: aggLoads } = hooks.aggQuery(
    { ...chartArgs, granularity: CHART_GRANULARITY },
    chartOpts
  );
  const { data: metersLoads } = hooks.metersAggQuery(
    { ...chartArgs, granularity: CHART_GRANULARITY },
    chartOpts
  );

  const aggSource = useMemo(() => {
    // Single meter selected: just that meter's per-meter series.
    if (chartMeterId) return (metersLoads ?? []).filter((l) => l.meter_id === chartMeterId);
    // CPEs aggregate: the dedicated aggregated endpoint (CPE-only) — unchanged.
    if (displayCPEs) return aggLoads ?? [];
    // Internal aggregate: the aggregated endpoint is hardcoded to CPE, so build
    // the internal aggregate by summing the per-meter series over INTERNAL meters
    // per timestamp (empty when there is no internal data).
    const internalIds = new Set(meters.filter((m) => m.type === "INTERNAL").map((m) => m.id));
    const byTs = new Map();
    (metersLoads ?? []).forEach((l) => {
      if (!internalIds.has(l.meter_id)) return;
      const cur = byTs.get(l.datetime_timestamp) || {
        datetime_timestamp: l.datetime_timestamp,
        consumption: 0,
        generation: 0,
      };
      cur.consumption += Number(l.consumption ?? 0);
      cur.generation += Number(l.generation ?? 0);
      byTs.set(l.datetime_timestamp, cur);
    });
    return Array.from(byTs.values()).sort((a, b) =>
      a.datetime_timestamp < b.datetime_timestamp ? -1 : 1
    );
  }, [aggLoads, metersLoads, chartMeterId, displayCPEs, meters]);

  const chartLabels = aggSource.map((l) =>
    createChartXAxisLabel(l.datetime_timestamp, CHART_GRANULARITY)
  );
  const netSeries = [
    {
      name: t("analytics.charts.net_series", { defaultValue: "carga líquida" }),
      data: aggSource.map((l) => +(l.consumption - l.generation).toFixed(3)),
      color: "var(--accent)",
    },
  ];
  const cgSeries = [
    {
      name: t("analytics.keymetrics.total_consumption", { defaultValue: "Consumo" }),
      data: aggSource.map((l) => +Number(l.consumption ?? 0).toFixed(3)),
      color: "var(--accent)",
    },
    {
      name: t("analytics.charts.generation_series", { defaultValue: "Geração" }),
      data: aggSource.map((l) => +Number(l.generation ?? 0).toFixed(3)),
      color: "var(--warm)",
    },
  ];

  const donutData = useMemo(() => {
    const wantedType = displayCPEs ? "CPE" : "INTERNAL";
    const totals = {};
    (metersLoads ?? []).forEach((l) => {
      const meter = meters.find((m) => m.id === l.meter_id);
      if (!meter || meter.type !== wantedType) return;
      if (!totals[l.meter_id]) totals[l.meter_id] = { consumption: 0, generation: 0 };
      totals[l.meter_id].consumption += Number(l.consumption ?? 0);
      totals[l.meter_id].generation += Number(l.generation ?? 0);
    });
    const entries = Object.entries(totals);
    return {
      consumption: entries
        .map(([id, v]) => ({ label: meterName(id), value: +v.consumption.toFixed(2) }))
        .filter((d) => d.value > 0),
      generation: entries
        .map(([id, v]) => ({ label: meterName(id), value: +v.generation.toFixed(2) }))
        .filter((d) => d.value > 0),
    };
  }, [metersLoads, meters, displayCPEs]);

  const k = kpis ?? {};
  const consumption = displayCPEs ? k.total_consumption_cpe : k.total_consumption_internal;
  const generation = displayCPEs ? k.total_generation_cpe : k.total_generation_internal;
  const net = displayCPEs ? k.total_net_consumption_cpe : k.total_net_consumption_internal;
  const topMeterId = displayCPEs
    ? k.meter_highest_consumption_cpe
    : k.meter_highest_consumption_internal;

  // --- table (server pagination) ---
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [cpe, setCpe] = useState(null);

  const { data: tableData, isFetching: tableFetching } = hooks.paginationQuery(
    {
      page,
      limit,
      date,
      time,
      cpe,
      meterType: displayCPEs ? "CPE" : "INTERNAL",
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    { refetchOnMountOrArgChange: true }
  );

  const tableRows = useMemo(
    () =>
      (tableData?.data ?? []).map((row, i) => ({
        id: row.id ?? i,
        date: formatDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        meter: meterName(row.meter_id),
        consumption: row.consumption,
        generation: row.generation,
        net: +(Number(row.consumption ?? 0) - Number(row.generation ?? 0)).toFixed(3),
      })),
    [tableData, meters]
  );

  const numCell = (v, digits, color) => (
    <span className="tabular-nums" style={color ? { color, fontWeight: 600 } : undefined}>
      {v != null ? Number(v).toFixed(digits) : "—"}
    </span>
  );

  const tableColumns = [
    { key: "date", label: t("table.columns.date"), sortable: true },
    { key: "time", label: t("table.columns.time"), sortable: true },
    {
      key: "meter",
      label: t("table.columns.meter", { defaultValue: "Contador" }),
      render: (v) => (
        <span className="font-[family-name:var(--mono)] text-[12.5px] text-ink">{v}</span>
      ),
    },
    {
      key: "consumption",
      label: t("table.columns.consumption", { defaultValue: "Consumo (kWh)" }),
      align: "right",
      render: (v) => numCell(v, 3),
    },
    {
      key: "generation",
      label: t("table.columns.generation", { defaultValue: "Geração (kWh)" }),
      align: "right",
      render: (v) => numCell(v, 3, v > 0 ? "var(--warm)" : undefined),
    },
    {
      key: "net",
      label: t("table.columns.net", { defaultValue: "Carga líquida (kWh)" }),
      align: "right",
      render: (v) => numCell(v, 3, v < 0 ? "var(--success)" : undefined),
    },
  ];

  const onTableFilter = (setter) => (v) => {
    setPage(0);
    setter(v || null);
  };

  const exportCsv = () => {
    const header = ["date", "time", "meter", "consumption", "generation", "net"];
    const lines = tableRows.map((r) => header.map((h) => r[h]).join(";"));
    const blob = new Blob([[header.join(";"), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "medicoes.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const scopeLbl = displayCPEs
    ? t("analytics.toggles.meters.cpes", { defaultValue: "CPEs" })
    : t("analytics.toggles.meters.internal", { defaultValue: "Contadores Internos" });

  const meterOptions = meters
    .filter((m) => (displayCPEs ? m.type === "CPE" : m.type === "INTERNAL"))
    .map((m) => ({ value: m.id, label: m.identifier }));

  return (
    <>
      <Tabs
        tabs={[
          { id: "charts", label: t("tabs.charts"), icon: "show_chart" },
          { id: "table", label: t("tabs.table"), icon: "table_rows" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "charts" ? (
        <div className="flex flex-col gap-rec">
          <KpiStrip
            cols={4}
            items={[
              {
                icon: "bolt",
                value: fmt(consumption),
                unit: "kWh",
                label: t("analytics.keymetrics.total_consumption", {
                  defaultValue: "Consumo total",
                }),
                tint: "teal",
              },
              {
                icon: "solar_power",
                value: fmt(generation),
                unit: "kWh",
                label: t("analytics.keymetrics.total_generation", {
                  defaultValue: "Geração total",
                }),
                tint: "amber",
              },
              {
                icon: "swap_vert",
                value: fmt(net),
                unit: "kWh",
                label: t("analytics.keymetrics.net_consumption", {
                  defaultValue: "Consumo líquido",
                }),
                tint: "info",
              },
              {
                icon: "electric_meter",
                value: topMeterId ? meterName(topMeterId) : "—",
                fitValue: true,
                fitLabel: true,
                label: t("analytics.keymetrics.top_meter", {
                  defaultValue: "Contador com maior consumo",
                }),
                tint: "green",
              },
            ]}
          />

          <div className="rec-card rec-card-pad">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
              <div>
                <div className="text-[15px] font-bold text-ink">
                  {view === "net"
                    ? t("analytics.charts.net_title", {
                        defaultValue: "Consumo Líquido Agregado ({{scope}})",
                        scope: scopeLbl,
                      })
                    : t("analytics.charts.cg_title", {
                        defaultValue: "Consumo e Geração ({{scope}})",
                        scope: scopeLbl,
                      })}
                </div>
                <div className="text-[12px] text-ink-3 mt-[2px]">{`kWh · ${sideLabel}`}</div>
              </div>
              <div className="flex items-center gap-[14px] flex-wrap">
                {view === "cg" && (
                  <div className="flex items-center gap-3 text-[12px] text-ink-2">
                    <span className="inline-flex items-center gap-[6px]">
                      <i
                        className="inline-block w-[10px] h-[10px] rounded-full"
                        style={{ background: "var(--accent)" }}
                      />
                      {t("analytics.keymetrics.total_consumption", { defaultValue: "Consumo" })}
                    </span>
                    <span className="inline-flex items-center gap-[6px]">
                      <i
                        className="inline-block w-[10px] h-[10px] rounded-full"
                        style={{ background: "var(--warm)" }}
                      />
                      {t("analytics.charts.generation_series", { defaultValue: "Geração" })}
                    </span>
                  </div>
                )}
                <Segmented
                  value={view}
                  options={[
                    {
                      value: "net",
                      label: t("analytics.toggles.loads.net", { defaultValue: "Cargas Líquidas" }),
                    },
                    {
                      value: "cg",
                      label: t("analytics.toggles.loads.separate", {
                        defaultValue: "Consumo e Geração",
                      }),
                    },
                  ]}
                  onChange={setView}
                />
                <select
                  className="rec-input !w-auto !h-[36px] text-[13px] cursor-pointer"
                  value={chartMeterId}
                  onChange={(e) => setChartMeterId(e.target.value)}
                >
                  <option value="">
                    {t("analytics.charts.all_meters", { defaultValue: "Todos os contadores" })}
                  </option>
                  {meterOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {view === "net" ? (
              <LineChart
                series={netSeries}
                labels={chartLabels}
                unit="kWh"
                height={320}
                type="bar"
              />
            ) : (
              <LineChart
                series={cgSeries}
                labels={chartLabels}
                unit="kWh"
                height={320}
                type="area"
              />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-rec">
            <DonutCard
              title={t("analytics.charts.doughnut_cons_title", {
                defaultValue: "Consumo por Contador (kWh)",
              })}
              data={donutData.consumption}
              unit="kWh"
            />
            <DonutCard
              title={t("analytics.charts.doughnut_gen_title", {
                defaultValue: "Geração por Contador (kWh)",
              })}
              data={donutData.generation}
              unit="kWh"
            />
          </div>
        </div>
      ) : (
        <div className="rec-card">
          <RemoteTable
            columns={tableColumns}
            rows={tableRows}
            totalCount={tableData?.total_count ?? 0}
            page={page}
            limit={limit}
            onPageChange={setPage}
            sort={sortOrder}
            onSortChange={(s) => {
              setPage(0);
              setSortOrder(s);
            }}
            loading={tableFetching}
            filters={
              <>
                <select
                  className="rec-input !w-auto !h-[36px] text-[13px] cursor-pointer"
                  value={date ?? ""}
                  onChange={(e) => onTableFilter(setDate)(e.target.value)}
                >
                  <option value="">
                    {t("filters.all_dates", { defaultValue: "Todas as datas" })}
                  </option>
                  {(tableData?.distinct_dates ?? []).map((o) => (
                    <option key={o.date} value={o.date}>
                      {formatDate(o.date)}
                    </option>
                  ))}
                </select>
                <select
                  className="rec-input !w-auto !h-[36px] text-[13px] cursor-pointer"
                  value={time ?? ""}
                  onChange={(e) => onTableFilter(setTime)(e.target.value)}
                >
                  <option value="">
                    {t("filters.all_times", { defaultValue: "Todas as horas" })}
                  </option>
                  {(tableData?.distinct_times ?? []).map((o) => (
                    <option key={o.time} value={o.time}>
                      {o.time}
                    </option>
                  ))}
                </select>
                <select
                  className="rec-input !w-auto !h-[36px] text-[13px] cursor-pointer"
                  value={cpe ?? ""}
                  onChange={(e) => onTableFilter(setCpe)(e.target.value)}
                >
                  <option value="">
                    {t("filters.all_meters", { defaultValue: "Todos os contadores" })}
                  </option>
                  {(tableData?.distinct_meter_identifiers ?? []).map((o) => (
                    <option key={o.meter_identifier} value={o.meter_identifier}>
                      {o.meter_identifier}
                    </option>
                  ))}
                </select>
                <div className="grow" />
                <UploadHelpButton />
                <label
                  htmlFor="meas-table-upload"
                  className="rec-btn-ghost rec-btn-sm"
                  style={isUploading ? { opacity: 0.4, pointerEvents: "none" } : undefined}
                >
                  <Icon name="upload_file" style={{ fontSize: 16 }} />
                  {t("upload_short", { defaultValue: "Carregar" })}
                  <input
                    id="meas-table-upload"
                    type="file"
                    accept=".csv"
                    multiple
                    hidden
                    onChange={onUpload}
                  />
                </label>
                <button type="button" className="rec-btn-ghost rec-btn-sm" onClick={exportCsv}>
                  <Icon name="download" style={{ fontSize: 16 }} />
                  {t("export_csv", { defaultValue: "Exportar CSV" })}
                </button>
              </>
            }
          />
        </div>
      )}
    </>
  );
}

MeasurementsSubView.propTypes = {
  hooks: PropTypes.shape({
    kpisQuery: PropTypes.func.isRequired,
    aggQuery: PropTypes.func.isRequired,
    metersAggQuery: PropTypes.func.isRequired,
    paginationQuery: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.oneOf(["cpes", "internal"]).isRequired,
  sideLabel: PropTypes.node.isRequired,
  onUpload: PropTypes.func.isRequired,
  isUploading: PropTypes.bool.isRequired,
};

/* Page-level measurements view (design's MeasurementsView): shared scope +
   date-range + upload/delete actions in the page head, side tabs switching
   between actual measurements and their forecast. */
function MeasurementsView({ initialSide }) {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["internalLoads", "erse", "sidenav"]);
  const { startDate, endDate, granularity, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [side, setSide] = useState(initialSide);
  const [scope, setScope] = useState("cpes");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const isForecast = side === "forecast";
  const taskType = isForecast ? "LOADS_FORECAST" : "LOADS";

  const sideTabs = [
    { id: "actual", icon: "electric_meter", label: t("title") },
    { id: "forecast", icon: "query_stats", label: t("forecast.title") },
  ];
  const sideLabel = isForecast ? t("forecast.title") : t("title");

  // --- upload (per active side, through the async task pipeline) ---
  const [addLoads] = useAddNewLoadsMutation();
  const [addLoadsForecast] = useAddNewLoadsForecastMutation();
  const onUpload = handleFileUpload(
    isForecast ? addLoadsForecast : addLoads,
    community.id,
    dispatch,
    taskType
  );

  const activeTasks = useSelector(selectActiveTasksByType(taskType));
  const {
    data: taskStatus,
    isFetching: taskFetching,
    isSuccess: taskSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess: taskSuccess,
    isFetching: taskFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE: taskType,
  });
  const isUploading = activeTasks.length > 0;

  // --- delete records (per active side) ---
  const [deleteLoads, { isLoading: isDeletingLoads }] = useDeleteLoadsMutation();
  const [deleteForecast, { isLoading: isDeletingForecast }] = useDeleteLoadsForecastMutation();
  const onConfirmDelete = async ({ startDate: ds, endDate: de }) => {
    const doDelete = isForecast ? deleteForecast : deleteLoads;
    try {
      await doDelete({ communityId: community.id, startDate: ds, endDate: de }).unwrap();
      dispatch(
        openNotificationPopup({
          notification: t("notifications.deleted_records", {
            defaultValue: "Medições eliminadas com sucesso.",
          }),
          type: "success",
        })
      );
      setDeleteModalOpen(false);
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err?.message ?? String(err), type: "error" }));
    }
  };

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("internalloads", { ns: "sidenav", defaultValue: "Medições Internas" }) },
          { label: t("title") },
        ]}
      />
      <PageHead
        title={t("title")}
        subtitle={t("page_subtitle", {
          defaultValue: "Visualizar e carregar consumos, gerações e respetivos forecasts",
        })}
        actions={
          <div className="flex items-center gap-[10px] flex-wrap">
            <Segmented
              value={scope}
              options={[
                {
                  value: "cpes",
                  label: t("analytics.toggles.meters.cpes", { defaultValue: "CPEs" }),
                },
                {
                  value: "internal",
                  label: t("analytics.toggles.meters.internal", {
                    defaultValue: "Contadores Internos",
                  }),
                },
              ]}
              onChange={setScope}
            />
            <DateRangePopover
              value={{ start: startDate, end: endDate }}
              onChange={(v) =>
                dispatch(
                  updateTimeSelector({
                    startDate: v.start,
                    endDate: v.end,
                    granularity,
                    horizon,
                    isExpanded,
                  })
                )
              }
            />
            <span className="w-px self-stretch bg-border my-1 mx-[2px]" aria-hidden="true" />
            {isUploading && (
              <Chip tone="amber">
                <Icon name="hourglass_top" style={{ fontSize: 13 }} />
                {t("uploading", { defaultValue: "A carregar…" })}
              </Chip>
            )}
            <a className="rec-btn-ghost rec-btn-sm no-underline" href="/docs/template_consumos.csv">
              <Icon name="download" style={{ fontSize: 16 }} />
              {t("list.buttons.template", { ns: "erse", defaultValue: "Template" })}
            </a>
            <UploadHelpButton />
            <label
              htmlFor="meas-upload"
              className="rec-btn-ghost rec-btn-sm"
              style={isUploading ? { opacity: 0.4, pointerEvents: "none" } : undefined}
            >
              <Icon name="upload_file" style={{ fontSize: 16 }} />
              {t("upload", { defaultValue: "Carregar dados" })}
              <input
                id="meas-upload"
                type="file"
                accept=".csv"
                multiple
                hidden
                onChange={onUpload}
              />
            </label>
            <button
              type="button"
              className="rec-btn-ghost rec-btn-sm"
              style={{ color: "var(--danger)" }}
              onClick={() => setDeleteModalOpen(true)}
            >
              <Icon name="delete" style={{ fontSize: 16 }} />
              {t("delete_records", { defaultValue: "Apagar" })}
            </button>
          </div>
        }
      />

      <SideTabs items={sideTabs} active={side} onChange={setSide}>
        <MeasurementsSubView
          key={side}
          hooks={HOOKS[side]}
          scope={scope}
          sideLabel={sideLabel}
          onUpload={onUpload}
          isUploading={isUploading}
        />
      </SideTabs>

      <DeleteRecordsModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={onConfirmDelete}
        isLoading={isForecast ? isDeletingForecast : isDeletingLoads}
        title={
          isForecast
            ? t("forecast.delete_title", { defaultValue: "Apagar forecast" })
            : t("delete_title", { defaultValue: "Apagar medições" })
        }
      />
    </div>
  );
}

MeasurementsView.propTypes = {
  initialSide: PropTypes.oneOf(["actual", "forecast"]),
};

MeasurementsView.defaultProps = {
  initialSide: "actual",
};

export default MeasurementsView;
